import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { RelatedKnowledgeAuto } from "@/components/site/RelatedKnowledgeAuto";
import { breadcrumbs, faqPage, serviceSchema, speakable, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad ingår i förproduktion?",
    a: "Manus och storyboard, produktionsplan och tidplan, budget, casting, location scouting, tillstånd och försäkring, teknikval, rekvisita och styling, samt call sheet till alla medverkande. Kort sagt: allt som måste vara klart innan kameran rullar.",
  },
  {
    q: "Hur stor del av budgeten går till förproduktion?",
    a: "Typiskt 15–25 procent av en filmbudget. Det är den del som är billigast att skära i och dyrast att ångra — de flesta överskridna filmbudgetar beror på beslut som skulle ha fattats i förproduktionen.",
  },
  {
    q: "Vad gör en linjeproducent?",
    a: "En linjeproducent ansvarar för att produktionen går att genomföra praktiskt och ekonomiskt: budget, bemanning, schema, avtal, tillstånd och logistik. Regissören ansvarar för det kreativa, linjeproducenten för att det blir av inom ramarna.",
  },
  {
    q: "Hur lång tid tar förproduktionen?",
    a: "En rak företagsfilm: 1–2 veckor. Reklamfilm med casting och flera locations: 3–6 veckor. Tillstånd för offentlig plats och drönarflygning kan ensamt kräva 2–4 veckors framförhållning.",
  },
  {
    q: "Behöver vi tillstånd för att filma?",
    a: "På allmän plats i Sverige krävs polistillstånd om inspelningen tar plats i anspråk eller påverkar trafik. Drönarflygning kräver operatörsregistrering och i vissa zoner särskilt tillstånd. Filmning på privat mark kräver markägarens medgivande, och medverkande behöver skriva på medgivande för publicering.",
  },
  {
    q: "Kan ni ta bara förproduktionen?",
    a: "Ja. Vi tar fristående uppdrag där vi levererar manus, plan, budget och call sheet som ni eller ert eget team sedan spelar in efter.",
  },
];

const TITLE = "Förproduktion av film — plan och budget | Fenny";
const DESCRIPTION =
  "Förproduktion av film: vad som ingår, hur lång tid det tar, vad en linjeproducent gör och vilka tillstånd som krävs vid inspelning i Sverige 2026.";

export const Route = createFileRoute("/forproduktion")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Förproduktion av film — Fenny" },
      { property: "og:description", content: "Planering, budget, casting och tillstånd innan kameran rullar." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://fenny.se/forproduktion" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/forproduktion" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path: "/forproduktion", name: TITLE, description: DESCRIPTION })) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Förproduktion av film",
            path: "/forproduktion",
            serviceType: "Förproduktion och produktionsplanering",
            description: "Produktionsplanering, budget, casting, location scouting och tillstånd inför filminspelning.",
            priceRange: "15 000–120 000 kr",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Manus och koncept", path: "/manus-och-koncept" },
            { name: "Förproduktion", path: "/forproduktion" },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <LandingPage
        eyebrow="Förproduktion"
        title={<>Allt som måste vara klart innan kameran rullar.</>}
        lede="Förproduktion är planeringen som avgör om inspelningsdagen blir lugn eller dyr: manus, plan, budget, casting, locations, tillstånd och call sheet. Vi tar det som del av en produktion — eller som fristående uppdrag åt team som spelar in själva."
        tldr={
          <p>
            Förproduktion omfattar manus, storyboard, produktionsplan, budget,
            casting, location scouting, tillstånd och call sheet. Den tar typiskt{" "}
            <strong>15–25 procent av filmbudgeten</strong> och{" "}
            <strong>1–6 veckor</strong> i tid beroende på omfattning. Tillstånd
            för allmän plats och drönare kräver ofta{" "}
            <strong>2–4 veckors framförhållning</strong>.
          </p>
        }
        ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
        ctaSecondary={{ label: "Manus och koncept", to: "/manus-och-koncept" }}
        trackingId="forproduktion"
        bullets={[
          { title: "Inga överraskningar på plats", body: "Call sheet, schema och backup-plan innan alla åker ut." },
          { title: "Budget som håller", body: "Kostnadsdrivarna identifieras i planen — inte i efterkalkylen." },
          { title: "Tillstånd i tid", body: "Polis, markägare, drönarzoner och medgivanden hanteras med marginal." },
        ]}
        pricing={{
          heading: "Vad kostar förproduktion?",
          intro: "Riktpriser exklusive moms som fristående uppdrag, Sverige 2026.",
          rows: [
            { format: "Företagsfilm", scope: "Manus, plan, schema, call sheet", price: "15 000–30 000 kr", lead: "1–2 veckor" },
            { format: "Reklamfilm", scope: "Koncept, storyboard, casting, locations, tillstånd, budget", price: "45 000–120 000 kr", lead: "3–6 veckor" },
            { format: "Casting", scope: "Brief, urval, provfilmning, avtal", price: "12 000–40 000 kr", lead: "1–3 veckor" },
            { format: "Location scouting", scope: "Research, platsbesök, bildunderlag, avtal", price: "8 000–25 000 kr", lead: "1–2 veckor" },
          ],
          footnote: "Förproduktion ingår i offerten när vi också producerar filmen.",
        }}
        process={{
          heading: "Förproduktionens sju steg",
          steps: [
            { label: "Brief och mål", body: "Vad filmen ska åstadkomma, för vem och i vilken kanal.", duration: "dag 1" },
            { label: "Manus och storyboard", body: "Innehållet fastställs och visualiseras innan resurser bokas.", duration: "vecka 1–2" },
            { label: "Budget och kalkyl", body: "Team, dagar, teknik, talanger, resa och post — post för post.", duration: "vecka 2" },
            { label: "Casting och locations", body: "Rätt ansikten och rätt miljöer, med avtal och medgivanden på plats.", duration: "vecka 2–4" },
            { label: "Tillstånd och försäkring", body: "Polistillstånd, markägare, drönarzoner, produktionsförsäkring.", duration: "vecka 2–5" },
            { label: "Teknikplan", body: "Kamera, ljus, ljud, rigg och logistik matchas mot storyboarden.", duration: "vecka 4" },
            { label: "Call sheet", body: "Tider, adresser, kontakter, väderplan och backup skickas till alla.", duration: "48 h före" },
          ],
        }}
        sections={[
          {
            id: "linjeproducent",
            heading: "Vad en linjeproducent gör",
            body: (
              <p>
                Linjeproducenten äger genomförbarheten: budget, bemanning,
                schema, avtal, tillstånd och logistik. På en reklamfilm är det
                ofta linjeproducenten som avgör om en kreativ idé går att
                genomföra på två dagar i stället för fyra — och därmed om
                projektet håller budget. På mindre produktioner slås rollen ihop
                med produktionsledaren.
              </p>
            ),
          },
          {
            id: "tillstand",
            heading: "Tillstånd vid inspelning i Sverige",
            body: (
              <>
                <p>
                  Allmän plats: polistillstånd krävs när inspelningen tar mark i
                  anspråk, spärrar av eller påverkar trafik. Ansök i god tid —
                  handläggning tar ofta veckor och en avgift tillkommer.
                </p>
                <p>
                  Drönare: operatörsregistrering krävs alltid, och flygning nära
                  flygplatser, över folksamlingar eller i restriktionsområden
                  kräver särskilt tillstånd. Medverkande: skriftligt medgivande
                  för publicering, och för barn även vårdnadshavares godkännande.
                </p>
              </>
            ),
          },
          {
            id: "spar-pengar",
            heading: "Var förproduktion sparar mest pengar",
            body: (
              <p>
                Genom att skära i inspelningsdagar snarare än i kvalitet: en
                genomarbetad{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/storyboard">storyboard</a>{" "}
                gör att man vet exakt vilka bilder som behövs och kan klara sig på
                en dag i stället för två. Genom att gruppera scener per location,
                och genom att bestämma versionering redan i{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/manus-och-koncept">manus och koncept</a>{" "}
                så att allt material fångas i samma inspelning.
              </p>
            ),
          },
        ]}
        faq={FAQ}
        caseLimit={4}
        riskReducers
        closing={{
          title: "Ska ni spela in något snart?",
          body: "Boka 30 minuter så går vi igenom vad förproduktionen behöver täcka.",
          primary: { label: "Boka strategimöte", to: "/kontakt" },
        }}
      />
      <RelatedKnowledgeAuto topics={["process", "manus", "produktion", "pris"]} />
    </>
  );
}
